import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API_BASE_URL from "../services/apiClient";
import BackHeader from "../components/hub/BackHeader";

const docTypes = [
  { name: "Passport", icon: "🛂", color: "text-blue-600 bg-blue-50 border-blue-100" },
  { name: "Visa", icon: "🎟️", color: "text-purple-600 bg-purple-50 border-purple-100" },
  { name: "Travel Insurance", icon: "🛡️", color: "text-emerald-600 bg-emerald-50 border-emerald-100" },
  { name: "Hotel Booking", icon: "🏨", color: "text-orange-600 bg-orange-50 border-orange-100" },
  { name: "Flight Ticket", icon: "✈️", color: "text-indigo-600 bg-indigo-50 border-indigo-100" },
  { name: "Other", icon: "📄", color: "text-gray-600 bg-gray-50 border-gray-100" },
];

const requiredDocs = [
  "Passport",
  "Visa",
  "Travel Insurance",
];

const DocumentsPage = ({ onTabChange }) => {
  const { travelKeyToken } = useParams();
  const navigate = useNavigate();

  const [documents, setDocuments] = useState([]);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
const [uploading, setUploading] = useState(false);
  const emptyDoc = {
    type: "Passport",
    fileUrl: "",
    notes: "",
  };

  const [newDoc, setNewDoc] = useState(emptyDoc);
const uploadedTypes = documents.map(d => d.type);

const missingDocs = requiredDocs.filter(
  (doc) => !uploadedTypes.includes(doc)
);
  /* ================= FETCH ================= */
  useEffect(() => {
    const fetchDocs = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const res = await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/documents`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          setDocuments(data);
        }
      } catch (err) {
        console.error("Fetch docs error:", err);
      } finally {
        setInitialLoaded(true);
        setLoading(false);
      }
    };

    fetchDocs();
  }, [travelKeyToken, navigate]);

  /* ================= AUTO SAVE ================= */
  useEffect(() => {
    if (!initialLoaded) return;

    const saveDocs = async () => {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      try {
        await fetch(`${API_BASE_URL}/api/trips/${travelKeyToken}/documents`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ documents }),
        });
      } catch (err) {
        console.error("Save docs error:", err);
      }
    };

    const timer = setTimeout(saveDocs, 1000);
    return () => clearTimeout(timer);
  }, [documents, initialLoaded, travelKeyToken]);

  /* ================= ACTIONS ================= */
  const handleAddDoc = () => {
    if (!newDoc.fileUrl) return;
    setDocuments([...documents, { ...newDoc, id: Date.now() }]);
    setNewDoc(emptyDoc);
    setShowAddForm(false);
  };

  const removeDoc = (id) => {
    setDocuments(documents.filter((d) => d.id !== id));
  };

  const getDocTheme = (typeName) => {
    return docTypes.find(t => t.name === typeName) || docTypes[5];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-20">
        <div className="w-10 h-10 border-4 border-black border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-xs font-black uppercase tracking-widest text-gray-400">Opening Vault...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {onTabChange && (
        <BackHeader title="Documents" onBack={() => onTabChange("menu")} />
      )}
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="text-4xl font-black tracking-tight text-gray-900 mb-2">Vault</h1>
          <p className="text-gray-500 font-medium">Securely store your essential travel documents and IDs.</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="bg-black text-white px-8 py-4 rounded-full font-bold shadow-2xl shadow-gray-200 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 w-fit"
        >
          <span className="text-xl">+</span> Add Document
        </button>
      </div>

      {/* Add Document Form Card */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="bg-white rounded-[32px] p-8 border border-gray-100 shadow-xl mb-12"
          >
            <div className="flex justify-between items-center mb-8">
              <h3 className="text-xl font-black">Secure Upload</h3>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-gray-400 hover:text-black transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
{missingDocs.length > 0 && (
  <div className="bg-red-50 border border-red-200 rounded-2xl p-5 mb-8">
    <p className="text-sm font-bold text-red-600 mb-2">
      ⚠️ Important documents missing
    </p>
    <ul className="text-xs text-red-500 font-medium list-disc pl-5">
      {missingDocs.map((doc, i) => (
        <li key={i}>{doc}</li>
      ))}
    </ul>
  </div>
)}
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

  {/* DOCUMENT TYPE */}
  <div className="space-y-2">
    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">
      Document Type
    </label>
    <select
      value={newDoc.type}
      onChange={(e) => setNewDoc({ ...newDoc, type: e.target.value })}
      className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold"
    >
      {docTypes.map((type) => (
        <option key={type.name} value={type.name}>
          {type.icon} {type.name}
        </option>
      ))}
    </select>
  </div>

  {/* DRIVE LINK INPUT */}
  <div className="space-y-2">
    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">
      File Link (Drive/Cloud)
    </label>
    <input
      placeholder="https://drive.google.com/..."
      value={newDoc.fileUrl}
      onChange={(e) =>
        setNewDoc({ ...newDoc, fileUrl: e.target.value })
      }
      className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300"
    />
  </div>

  {/* 🔥 CLOUDINARY FILE UPLOAD */}
  <div className="md:col-span-2 space-y-2">
    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">
      Or Upload File (PDF / Image)
    </label>

    <input
      type="file"
      accept="image/*,.pdf"
     onChange={async (e) => {
  const file = e.target.files[0];
  if (!file) return;
if (file.size > 5 * 1024 * 1024) {
  alert("Max 5MB file allowed");
  return;
}
  setUploading(true);

  try {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "expeditio_upload");

    const res = await fetch(
      "https://api.cloudinary.com/v1_1/dm5ifgtgw/auto/upload",
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await res.json();

    setNewDoc((prev) => ({
      ...prev,
      fileUrl: data.secure_url,
    }));

  } catch (err) {
    console.error("Upload error:", err);
  } finally {
    setUploading(false);
  }
}}
      className="w-full bg-gray-50 border-none rounded-2xl px-5 py-3 font-bold"
    />

    {/* SUCCESS MESSAGE */}
    {uploading && (
  <p className="text-xs text-gray-500 font-bold">
    ⏳ Uploading...
  </p>
)}

{!uploading && newDoc.fileUrl && (
  <p className="text-xs text-green-600 font-bold">
    ✅ File ready
  </p>
)}

{newDoc.fileUrl && (
  <a
    href={newDoc.fileUrl}
    target="_blank"
    rel="noopener noreferrer"
    className="text-xs text-blue-600 font-bold underline"
  >
    🔍 Preview file
  </a>
)}
  </div>

  {/* NOTES */}
  <div className="md:col-span-2 space-y-2">
    <label className="text-[10px] font-black uppercase text-gray-400 tracking-widest px-2">
      Notes
    </label>
    <textarea
      placeholder="Expiry dates, reference numbers, or other details..."
      value={newDoc.notes}
      onChange={(e) =>
        setNewDoc({ ...newDoc, notes: e.target.value })
      }
      className="w-full bg-gray-50 border-none rounded-2xl px-5 py-4 focus:ring-2 focus:ring-black transition-all font-bold placeholder:text-gray-300 min-h-[100px]"
    />
  </div>

</div>

            <div className="mt-8 flex justify-end gap-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-8 py-4 rounded-full font-bold text-gray-400 hover:text-black transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleAddDoc}
                className="bg-black text-white px-10 py-4 rounded-full font-bold shadow-xl active:scale-95 transition-all"
              >
                Save to Vault
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Documents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {documents.length === 0 ? (
          <div className="md:col-span-2 bg-white rounded-[32px] p-24 border-2 border-dashed border-gray-100 flex flex-col items-center text-center">
            <div className="text-6xl mb-6 opacity-20">📂</div>
            <h3 className="text-3xl font-black text-gray-900 mb-2">Your vault is empty</h3>
            <p className="text-gray-400 font-medium mb-10 max-w-sm">
              Keep copies of your Passport, Visa, and Insurance safe and accessible everywhere.
            </p>
            <button
              onClick={() => setShowAddForm(true)}
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-full font-bold hover:bg-gray-200 transition-all"
            >
              + Upload First Document
            </button>
          </div>
        ) : (
          documents.map((doc) => {
            const theme = getDocTheme(doc.type);
            return (
              <motion.div
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                key={doc.id}
                className="group bg-white rounded-[32px] p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:shadow-gray-100 transition-all flex flex-col justify-between overflow-hidden relative"
              >
                {/* File Icon Accent */}
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                  <span className="text-7xl select-none">{theme.icon}</span>
                </div>

                <div>
                  <div className="flex items-center gap-4 mb-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl border ${theme.color}`}>
                      {theme.icon}
                    </div>
                    <div>
                      <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest">{doc.type}</p>
                      <h4 className="text-xl font-black text-gray-900 truncate max-w-[180px]">
                        {doc.notes ? (doc.notes.split('\n')[0].length > 20 ? doc.notes.slice(0, 20) + "..." : doc.notes.split('\n')[0]) : doc.type}
                      </h4>
                    </div>
                  </div>

                  {doc.notes && (
                    <p className="text-sm text-gray-500 font-medium line-clamp-2 mb-6 h-10">
                      {doc.notes}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between mt-4">
                  <a
                    href={doc.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-black text-white px-6 py-3 rounded-full text-xs font-bold flex items-center gap-2 hover:scale-105 active:scale-95 transition-all shadow-lg shadow-gray-200"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    View File
                  </a>

                  <button
                    onClick={() => removeDoc(doc.id)}
                    className="p-3 text-gray-200 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default DocumentsPage;